import { useTranslate } from '@tolgee/react';
import { useRouter } from 'next/router';
import { useSession } from 'next-auth/react';
import { useMemo } from 'react';
import styled from 'styled-components';

import FoldedHandsSVG from '../../assets/icons/FoldedHands';
import LiveEmptyState from '../../components/_molecules/LiveEmptyState';
import NewAppLayout from '../../components/layouts/NewAppLayout';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import useSignIn from '../../hooks/useSignIn';
import useTenants from '../../hooks/useTenants';

const NoAccess = ({
  children,
  description,
  condition,
  isLoading,
}: {
  children: React.ReactChild;
  description?: string;
  condition?: boolean;
  isLoading?: boolean;
}) => {
  const { signIn } = useSignIn();
  const { t } = useTranslate();
  const router = useRouter();
  // When the new auth experience is on, send logged-out users to the in-app
  // /login screen instead of the legacy Azure B2C hosted flow.
  const newAuth = useFeatureFlag('US_AUTH');

  const tenantByUrl = router.query.tenantId;
  const { hasAccessToTenantByUrl } = useTenants();
  useSession({
    required: true,
    onUnauthenticated() {
      if (newAuth) {
        router.replace(`/login?redirect=${encodeURIComponent(router.asPath)}`);
      } else {
        signIn();
      }
    },
  });
  const hasAccess = useMemo(() => {
    if (isLoading) return true;
    if (condition === false) return false;
    if (tenantByUrl && hasAccessToTenantByUrl === false) return false;
    return true;
  }, [condition, hasAccessToTenantByUrl, isLoading, tenantByUrl]);

  if (!hasAccess)
    return (
      <NewAppLayout>
        <NoAccessContainer>
          <LiveEmptyState
            title={t('No access', 'No access')}
            icon={<FoldedHandsSVG />}
            description={
              description
                ? description
                : t('YouDontHaveAccess', 'You dont have access to this space')
            }
          />
        </NoAccessContainer>
      </NewAppLayout>
    );
  return <>{children}</>;
};
const NoAccessContainer = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  justify-content: center;
  padding: 20px;
  align-items: center;
`;

export default NoAccess;
